// @ts-strict-ignore
import isObject from 'lodash/isObject';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';

import {getIndexFromOrdinalLabel} from 'common/util';
import {SortBy, SortByColumnsConfig, SortByValueConfig, SortConfig} from 'common/types/reports/bookmark';

import {HeaderType, typedComparator} from './header-sorting';
import {OrderedSumTree, UnorderedSumTree} from './types';

/**
 * Given an object with numeric leaf values, transform each leaf to zero
 */
export function deepMapValuesToZero(obj: Obj | number) {
  return isObject(obj) ? mapValues(obj, (innerValue) => deepMapValuesToZero(innerValue)) : 0;
}

/**
 * Flatten a sum tree such that each node has at most one child.
 * Used to calculate sort by value.
 */
function getFlattenedOrderedSumTree(tree: UnorderedSumTree): OrderedSumTree[] {
  const nodes = Object.values(tree);

  if (nodes[0].children) {
    return nodes.flatMap((node) => {
      return getFlattenedOrderedSumTree(node.children).map((child) => ({
        ...node,
        value: child.value,
        timeComparisonValue: child.timeComparisonValue,
        liftComparisonValue: child.liftComparisonValue,
        children: [child],
      }));
    });
  }
  return nodes.map((node) => omit(node, `children`));
}

function sortSumTreeByColumn(
  tree: UnorderedSumTree,
  sortConfig: SortByColumnsConfig,
  hasOrdinalPrefix = false,
): OrderedSumTree[] {
  const currColSortAttrs = sortConfig.colSortAttrs[0];
  const nodes = Object.values(tree);
  let sortType = currColSortAttrs.sortBy === SortBy.Label ? nodes[0].headerType : HeaderType.NUMBER;
  if (hasOrdinalPrefix && currColSortAttrs.sortBy === SortBy.Label) {
    // when sorting by label with ordinal prefix (eg "A. foo event"), we sort by the index of the ordinal label
    sortType = HeaderType.NUMBER;
  }

  return nodes
    .map((node) => {
      if (node.children && !isEmpty(node.children)) {
        return {
          ...node,
          children: sortSumTreeByColumn(node.children, {
            ...sortConfig,
            colSortAttrs: sortConfig.colSortAttrs.slice(1),
          }),
        };
      }

      return omit(node, `children`);
    })
    .sort(
      typedComparator({
        sortType,
        order: currColSortAttrs.sortOrder,
        transform: (item) => {
          if (currColSortAttrs.sortBy === SortBy.Value && sortConfig.valueField) {
            return item[sortConfig.valueField].toString();
          } else if (hasOrdinalPrefix && currColSortAttrs.sortBy === SortBy.Label) {
            return getIndexFromOrdinalLabel(item.label);
          }
          return item[currColSortAttrs.sortBy].toString();
        },
      }),
    );
}

function sortSumTreeByValue(tree: UnorderedSumTree, sortConfig: SortByValueConfig): OrderedSumTree[] {
  return getFlattenedOrderedSumTree(tree).sort(
    typedComparator({
      sortType: HeaderType.NUMBER,
      order: sortConfig.sortOrder,
      transform: (item) => {
        if (sortConfig.valueField) {
          return item[sortConfig.valueField].toString();
        }
        return item.value.toString();
      },
    }),
  );
}

export function sortSumTree(
  tree: UnorderedSumTree,
  sortConfig: SortConfig,
  hasOrdinalPrefix = false,
): OrderedSumTree[] {
  if (isEmpty(tree)) {
    return [];
  }

  switch (sortConfig.sortBy) {
    case SortBy.Value:
      return sortSumTreeByValue(tree, sortConfig);
    case SortBy.Column:
      return sortSumTreeByColumn(tree, sortConfig, hasOrdinalPrefix);
    default:
      throw new Error(`Unsupported sortBy type: ${sortConfig[`sortBy`]}`);
  }
}
